import { ArrowTopRightOnSquareIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useUser } from "@/api/hooks/useUser";
import { useWarrantyCasesBuilding } from "@/api/hooks/useWarrantyCases.ts";
import { EntityConfigurationType, useBuildingConfiguration } from "@/api/rest/services/configurationService.ts";
import { getAllQuestionsForBuilding } from "@/api/rest/services/questionService.ts";
import Button from "@/components/common/Button";
import ListQuestions from "@/components/question-contents/ListQuestions.tsx";
import ListClaims from "@/components/warranty-contents/ListClaims.tsx";
import { useDebugLifecycle } from "@/utils/debug/useDebugLifecycle";

export default function BuildingPage() {
  const { t } = useTranslation();
  const { buildingId } = useParams<{ buildingId: string }>();
  const navigate = useNavigate();

  const account = useUser();
  const [cases] = useWarrantyCasesBuilding(Number(buildingId));

  const { data: buildingConfig } = useBuildingConfiguration(Number(buildingId));

  const questionsEnabled = useMemo(() => {
    if (!buildingConfig) {
      return false;
    }
    const config = buildingConfig.find(
      (conf) => conf.type === EntityConfigurationType.ESCALATION_QUESTION_EMAIL_FORWARDING_RULE,
    );
    if (config?.value) {
      try {
        const parsed = JSON.parse(config.value) as { enabled: boolean };
        return parsed.enabled;
      } catch (e) {
        console.error("Failed to parse forwarding rules:", e);
        return false;
      }
    }
    return false;
  }, [buildingConfig]);

  const lookerReportLink = useMemo(() => {
    const config = buildingConfig?.find((conf) => conf.type === EntityConfigurationType.LOOKER_REPORT_LINK);
    if (!config?.value) {
      return null;
    }
    const trimmedValue = config.value.trim();
    try {
      const url = new URL(trimmedValue);
      return url.toString();
    } catch {
      return null;
    }
  }, [buildingConfig]);

  const internalAnalyticsEnabled = useMemo(() => {
    const config = buildingConfig?.find((conf) => conf.type === EntityConfigurationType.INTERNAL_ANALYTICS_ENABLED);
    return config?.value === "true";
  }, [buildingConfig]);

  const { data: questionsData } = useQuery({
    queryKey: ["questions-building", buildingId],
    queryFn: () => getAllQuestionsForBuilding(Number(buildingId)),
    enabled: Boolean(buildingId) && questionsEnabled,
  });
  const questions = questionsData?.result;

  const currentBuilding = useMemo(
    () => account?.accounts.flatMap((acc) => acc.managedBuildings).find((b) => b.buildingId === Number(buildingId)),
    [account, buildingId],
  );

  useEffect(() => {
    if (account && !currentBuilding) {
      navigate("/");
    }
  }, [account, currentBuilding, navigate]);

  useDebugLifecycle("BuildingPage", {
    data: {
      url: window.location.href,
    },
  });

  return (
    <main className="flex flex-grow flex-col p-4 md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white items-center">
      <div className="flex flex-grow self-stretch flex-col">
        <div className="flex flex-row items-center mb-8">
          <h2 className="text-3xl font-bold text-center mr-4">{currentBuilding?.street}</h2>
        </div>

        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">{t("building.options")}</h3>
          <div className="flex flex-wrap gap-3">
            <Button className="gap-2 rounded-lg" onClick={() => navigate(`/building/${buildingId}/settings`)}>
              {t("building.settings")}
            </Button>
            {internalAnalyticsEnabled && (
              <Button variant="outline" className="gap-2 rounded-lg" onClick={() => navigate("/analytics/")}>
                <ChartBarIcon className="h-5 w-5" />
                {t("building.analytics")}
              </Button>
            )}
            {lookerReportLink && (
              <a
                href={lookerReportLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-custom inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                <ChartBarIcon className="h-5 w-5" />
                {t("building.lookerReport")}
                <ArrowTopRightOnSquareIcon className="h-4 w-4 opacity-70" />
              </a>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex flex-grow basis-1/2 flex-col gap-4 items-stretch">
            <ListClaims cases={cases ?? []} showHouseholds user={account} building={currentBuilding} />
          </div>
          {questionsEnabled && (
            <div className="flex flex-grow basis-1/2 flex-col gap-4 items-stretch">
              <ListQuestions questions={questions ?? []} showHouseholds user={account} building={currentBuilding} />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
